import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Ellipsis, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ACL_RELATIONSHIPS from "acl-relationships";
import Authorized from "components/Authorized";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import { formatError } from "helpers/formatError";
import { useContactNavigation } from "routes/contact/useContactNavigation";

const EDIT_ACLS = [
  ACL_RELATIONSHIPS.entityContact.edit,
  ACL_RELATIONSHIPS.entityForFilters.read,
];
const DELETE_ACLS = [ACL_RELATIONSHIPS.entityContact.delete];

export function ContactTableActions({ contact, handleDeleteContact }) {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { openEdit } = useContactNavigation();

  const remove = useMutation({
    mutationFn: () => handleDeleteContact(contact.entity_id, contact.id),
    onSuccess: () => {
      setIsDeleteOpen(false);
      toast.success("Contact deleted");
    },
    onError: error =>
      toast.error(formatError(error, "Could not delete the Contact")),
  });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Contact actions"
          className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
        >
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="tw:w-auto">
          <Authorized permissions={EDIT_ACLS}>
            <DropdownMenuItem onClick={() => openEdit(contact.id)}>
              <Pencil />
              Edit
            </DropdownMenuItem>
          </Authorized>
          <Authorized permissions={DELETE_ACLS}>
            <DropdownMenuItem
              variant="destructive"
              onClick={() => setIsDeleteOpen(true)}
            >
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </Authorized>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDeleteDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title="Delete Contact?"
        description="Are you sure you want to delete this Contact?"
        onConfirm={() => remove.mutate()}
      />
    </>
  );
}
